import { useState, useRef, useEffect, useCallback, type MouseEvent, type ReactNode, type Ref } from 'react';
import { ChatBubbleOutlineIcon } from '@/components/ui/Icons';
import { Button } from '@/components/ui/Button/Button';
import { ChatPopover } from '@/components/Messenger/popover/ChatPopover';
import { MessengerNewMessageOverlay } from '@/components/Messenger/overlay/MessengerNewMessageOverlay';
import { MessengerUnreadBadge } from '@/components/Messenger/shared/MessengerUnreadBadge';
import { MessengerNavbarStackProvider } from '@/contexts/MessengerNavbarStackContext';
import { useAuth } from '@/contexts/useAuth';
import { useMessengerUnreadTotal } from '@/hooks/useMessengerUnreadTotal';
import {
  MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION,
  MESSENGER_OPEN_THREAD_FROM_NOTIFICATION,
  type MessengerOpenAgentChatFromNotificationDetail,
  type MessengerOpenThreadFromNotificationDetail,
} from '@/constants/messengerEvents';
import '@/components/Messenger/styles/ChatFab.css';

/** Всё, что нужно кнопке мессенджера: шапка нового дизайна рисует свою кнопку (ChatFab renderTrigger) */
export interface ChatFabTriggerProps {
  ref: Ref<HTMLButtonElement>;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  open: boolean;
  unread: number;
}

/** Кнопка мессенджера, его окно и всплывающие новые сообщения. renderTrigger — своя кнопка; по умолчанию — старой шапки. */
export function ChatFab({ renderTrigger }: { renderTrigger?: (props: ChatFabTriggerProps) => ReactNode } = {}) {
  const { user, isAuthenticated, capabilities } = useAuth();
  const personId = isAuthenticated ? user?.id ?? null : null;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openThreadIntent, setOpenThreadIntent] =
    useState<MessengerOpenThreadFromNotificationDetail | null>(null);
  const [openAgentChatIntent, setOpenAgentChatIntent] =
    useState<MessengerOpenAgentChatFromNotificationDetail | null>(null);
  const chatPopoverStateRef = useRef<{ isExiting: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const { totalUnread } = useMessengerUnreadTotal();

  const consumeOpenThreadIntent = useCallback(() => setOpenThreadIntent(null), []);
  const consumeOpenAgentChatIntent = useCallback(() => setOpenAgentChatIntent(null), []);

  useEffect(() => {
    const onOpenFromNotification = (e: Event) => {
      const d = (e as CustomEvent<MessengerOpenThreadFromNotificationDetail>).detail;
      if (!d?.threadId) return;
      setOpenThreadIntent(d);
      setAnchorEl((prev) => prev ?? triggerRef.current);
    };
    const onOpenAgentFromNotification = (e: Event) => {
      const d = (e as CustomEvent<MessengerOpenAgentChatFromNotificationDetail>).detail;
      if (!d?.chatId) return;
      setOpenAgentChatIntent(d);
      setAnchorEl((prev) => prev ?? triggerRef.current);
    };
    window.addEventListener(MESSENGER_OPEN_THREAD_FROM_NOTIFICATION, onOpenFromNotification);
    window.addEventListener(MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION, onOpenAgentFromNotification);
    return () => {
      window.removeEventListener(MESSENGER_OPEN_THREAD_FROM_NOTIFICATION, onOpenFromNotification);
      window.removeEventListener(MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION, onOpenAgentFromNotification);
    };
  }, []);

  /** Cold start from SW `?agentChat=` deep link. */
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const agentChat = params.get('agentChat')?.trim();
      if (!agentChat) return;
      setOpenAgentChatIntent({ chatId: agentChat });
      setAnchorEl((prev) => prev ?? triggerRef.current);
      params.delete('agentChat');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', next);
    } catch {
      /* ignore */
    }
  }, []);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (chatPopoverStateRef.current?.isExiting) {
      setAnchorEl(e.currentTarget);
    } else {
      setAnchorEl((prev) => (prev === e.currentTarget ? null : e.currentTarget));
    }
  };

  const popoverOpen = Boolean(anchorEl);

  const handleOpenMessengerFromOverlay = useCallback(() => {
    setAnchorEl((prev) => prev ?? triggerRef.current);
  }, []);

  if (!isAuthenticated || !capabilities.hasAnyGrant) {
    return null;
  }

  return (
    <MessengerNavbarStackProvider>
      {renderTrigger ? (
        renderTrigger({ ref: triggerRef, onClick: handleClick, open: popoverOpen, unread: totalUnread })
      ) : (
        <Button
          ref={triggerRef}
          type="button"
          variant="outlined"
          size="s"
          className="navbar__header-icon-btn chat-fab"
          onClick={handleClick}
          title="Мессенджер"
          aria-label="Открыть мессенджер"
          aria-expanded={popoverOpen}
          aria-haspopup="dialog"
        >
          <ChatBubbleOutlineIcon size={18} aria-hidden />
          <MessengerUnreadBadge count={totalUnread} className="chat-fab__badge" />
        </Button>
      )}
      <ChatPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        stateRef={chatPopoverStateRef}
        openThreadIntent={openThreadIntent}
        onConsumedOpenThreadIntent={consumeOpenThreadIntent}
        openAgentChatIntent={openAgentChatIntent}
        onConsumedOpenAgentChatIntent={consumeOpenAgentChatIntent}
      />
      <MessengerNewMessageOverlay
        personId={personId}
        triggerRef={triggerRef}
        popoverOpen={popoverOpen}
        onOpenMessenger={handleOpenMessengerFromOverlay}
      />
    </MessengerNavbarStackProvider>
  );
}
